import { useMutation } from '@apollo/client';
import { Goal } from '../../goals';
import {
    CreateGoalDocument,
    CreateGoalMutationVariables,
    GoalFieldsFragmentDoc,
} from '../../graphql/generated';

/**
 * Mutation helper to create a goal.
 * Ensures the Apollo cache for the goals *list* is updated when a new goal is
 * added. This is necessary to avoid refetching queries that consume goals (like
 * AllEntitiesQuery) that would impose a multi-second additional roundtrip.
 */
export const useCreateGoal = <TGoal extends Goal>() => {
    const [createGoal] = useMutation(CreateGoalDocument, {
        update(cache, { data }) {
            if (!data?.createGoal?.goal) return;

            // Write new goal directly to cache for goals list to avoid
            // AllFinancialEntities query roundtrip.
            // Details: https://www.apollographql.com/docs/react/data/mutations/#the-update-function
            cache.modify({
                fields: {
                    goals(existingGoals = []) {
                        const newGoalRef = cache.writeFragment({
                            data: data.createGoal?.goal,
                            fragment: GoalFieldsFragmentDoc,
                            fragmentName: 'GoalFields',
                        });
                        return [...existingGoals, newGoalRef];
                    },
                },
            });
        },
    });

    return async ({
        params,
        onComplete,
        onError,
    }: {
        params: CreateGoalMutationVariables;
        onComplete: (goal: TGoal) => void;
        onError: (err: Error) => void;
    }) => {
        createGoal({
            variables: params,
            onCompleted: (data) => {
                const goal = data.createGoal?.goal;
                if (!goal) {
                    onError(new Error('Goal was not created'));
                    return;
                }
                onComplete(goal as unknown as TGoal);
            },
            onError,
        });
    };
};
